/**
 * The auction driver — Atlas ch.4, B4 through B10.
 *
 * Composes the pieces: consent gates it, Prebid runs it, the key-value budget
 * bounds what reaches the wire, GPT sends it, and the collector records what
 * happened. Prebid and GPT are injected rather than reached through globals, so
 * the whole sequence is testable without a browser.
 *
 * Two rules govern every branch below:
 *
 * **Never leave a slot empty.** A hanging bidder must not cost the publisher an
 * impression, so the GPT refresh is guaranteed to fire — on the bid callback if
 * bids arrive, on the failsafe timer if they do not, and exactly once either way.
 * A slot with no bidders still goes to GAM: AdX and direct-sold compete for it
 * there (ch.6), so it joins the same batched refresh without a Prebid auction.
 *
 * **Never throw into the publisher's page.** Every callback is wrapped. A
 * measurement bug that breaks someone's site is a far worse outcome than a lost
 * metric.
 */
import { BidCollector } from "./BidCollector.js";
import { fitToBudget } from "./KeyValueBudget.js";

/** Beyond Prebid's own timeout. Prebid should always finish first; this exists
 *  for the case where it does not call back at all. */
const FAILSAFE_MARGIN_MS = 1000;

/** After GAM answers a slot, how long to wait for Prebid's `bidWon` before the
 *  slot's bid batch is sent. The Prebid creative calls `renderAd` just after
 *  the slot renders, so the winner is known within this window or never. */
export const WIN_GRACE_MS = 1500;

/** The latest a slot's bid batch is held after the GAM request, whatever GAM
 *  does. A slot GAM never answers must still report its bids. */
export const REPORT_DEADLINE_MS = 8000;

const swallow = (fn, onError) => {
  try {
    return fn();
  } catch (err) {
    onError?.(err);
    return undefined;
  }
};

let auctionSeq = 0;
function newAuctionId() {
  auctionSeq += 1;
  const rand = Math.random().toString(36).slice(2, 10);
  return `pb-${Date.now().toString(36)}-${auctionSeq}-${rand}`;
}

/** `pbjs.version` if the page's Prebid exposes one.
 *
 *  Reading it is deliberately total: this runs inside someone else's page, and
 *  a getter that throws must cost them a measurement field, never an auction. */
export function prebidVersionOf(pbjs) {
  try {
    const v = pbjs?.version;
    return typeof v === "string" && v ? v.slice(0, 32) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * @param {object} deps
 * @param {object} deps.pbjs        Prebid's global (injected). May be absent: GAM-only.
 * @param {object} deps.googletag   GPT's global (injected).
 * @param {(batch: object) => void} [deps.sendBids]  POST /v1/bids. Fire and forget.
 * @param {(msg: string, err?: unknown) => void} [deps.onError]
 * @param {() => number} [deps.now]
 * @param {(fn: Function, ms: number) => unknown} [deps.setTimeoutFn]
 */
export class AuctionDriver {
  constructor({ pbjs, googletag, sendBids, onError, now = () => Date.now(), setTimeoutFn, clearTimeoutFn, requestAds }) {
    this.pbjs = pbjs;
    this.googletag = googletag;
    this.sendBids = sendBids ?? (() => {});
    this.onError = onError ?? (() => {});
    this.now = now;
    // Wrapped, not stored bare. Assigning `setTimeout` to a property and then
    // calling `this.setTimeoutFn(...)` invokes it with the driver as `this`,
    // and a real browser answers "TypeError: Illegal invocation" — jsdom does
    // not, so every unit test passed while the browser failed on the first
    // auction. Never store a host function as a bare property.
    this.setTimeoutFn = setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimeoutFn = clearTimeoutFn ?? ((id) => clearTimeout(id));
    // How the batched GAM request is made. A batched refresh by default; a page
    // that left GPT's initial load on needs display() instead (PageAdServer).
    this.requestAds = requestAds ?? ((slots) => this.googletag?.pubads?.().refresh?.(slots));
  }

  /**
   * Run one auction across every slot passed in.
   *
   * ONE `requestBids` for the slots that have bidders, and ONE batched GPT
   * refresh for all of them — batching is not just fewer round trips: it is
   * what lets GAM honour roadblocks and competitive exclusions across the page,
   * which it cannot do if each slot arrives on its own.
   *
   * Resolves when the refresh has been sent. Each slot's bid batch goes out
   * later, once GAM has answered that slot (see `WIN_GRACE_MS`).
   *
   * @param {object} args
   * @param {Array<{slot: string, requestId: string, divId: string, adUnitPath: string, sizes: number[][], bidders?: Array<{bidder: string, params: object}>, mediaType?: "banner"|"video", video?: object, renderer?: object, floorCpm?: number}>} args.units
   * @param {import("./ConsentGate.js").ConsentResult} args.consent
   * @param {number} [args.timeoutMs]
   * @param {number} [args.samplingRate]
   */
  async run({ units, consent, timeoutMs = 1200, samplingRate = 1 }) {
    if (!Array.isArray(units) || units.length === 0) return { collectors: [], refreshed: false };

    const auctionId = newAuctionId();
    const withBidders = units.filter((u) => Array.isArray(u.bidders) && u.bidders.length > 0);
    const collectors = new Map();
    for (const u of withBidders) {
      collectors.set(
        u.divId,
        new BidCollector({
          slot: u.slot,
          requestId: u.requestId,
          bidders: u.bidders.map((b) => b.bidder),
          now: this.now,
        }),
      );
    }
    const byDivId = new Map(units.map((u) => [u.divId, u]));
    this.#subscribe(auctionId, collectors);

    // The GPT refresh must happen exactly once, whichever path gets there
    // first — and whichever path gets there must also settle `run()`. Firing
    // the refresh without resolving would leave the caller awaiting forever,
    // which is how the failsafe stopped being a safety net.
    let done = false;
    let settle;
    const settled = new Promise((r) => {
      settle = r;
    });
    let failsafe;
    const finish = (reason) => {
      if (done) return;
      done = true;
      if (failsafe !== undefined) this.clearTimeoutFn(failsafe);
      swallow(() => this.#applyTargetingAndRefresh(units, withBidders, collectors, consent), (e) =>
        this.onError("targeting/refresh failed", e),
      );
      settle({ collectors: [...collectors.values()], refreshed: true, reason, auctionId });
    };

    const canBid = withBidders.length > 0 && typeof this.pbjs?.requestBids === "function";
    if (!canBid) {
      // Nothing to auction (no bidders on any slot, or Prebid never loaded):
      // GAM still decides every slot, straight away.
      finish(withBidders.length > 0 ? "no-prebid" : "gam-only");
    } else {
      // Fires only if Prebid never calls back at all. A hanging bidder must
      // never cost the publisher the impression.
      failsafe = this.setTimeoutFn(() => finish("failsafe"), timeoutMs + FAILSAFE_MARGIN_MS);
      const adUnits = withBidders.map((u) => toPrebidAdUnit(u));
      swallow(
        () =>
          this.pbjs.requestBids({
            adUnits,
            auctionId,
            timeout: timeoutMs,
            // Absent consent, Prebid's own GDPR enforcement drops the vendors
            // that have no legal basis; we never hand-roll that logic (D3).
            ...(consent?.tcString ? { gdprConsent: { consentString: consent.tcString } } : {}),
            bidsBackHandler: () => finish("bids"),
          }),
        (e) => {
          // requestBids threw synchronously. The failsafe is now the only path
          // to a refresh, so it must be left armed rather than finishing here —
          // the page still deserves the full timeout for GPT-side demand.
          this.onError("requestBids threw", e);
        },
      );
    }

    const result = await settled;
    this.#scheduleReports(collectors, byDivId, samplingRate);
    return result;
  }

  /**
   * Send each slot's bid batch once its outcome is knowable: GAM has answered
   * the slot and Prebid has had `WIN_GRACE_MS` to report a win — or the hard
   * deadline passed. Sent any earlier, every bid reads `lost_prebid`: GAM has
   * not chosen yet, so neither `won` nor `lost_gam` can exist.
   */
  #scheduleReports(collectors, byDivId, samplingRate) {
    for (const [divId, c] of collectors) {
      const u = byDivId.get(divId);
      let sent = false;
      const send = () => {
        if (sent) return;
        sent = true;
        this.clearTimeoutFn(deadline);
        if (grace !== undefined) this.clearTimeoutFn(grace);
        c.finalize();
        swallow(
          () =>
            this.sendBids({
              ...c.toBatch({ samplingRate }),
              adUnitPath: c.adUnitPath ?? u?.adUnitPath,
              // Which Prebid build our consent config actually landed in. We
              // pin no version (Atlas decision 42), so a page below a
              // framework's floor applies none of it and says nothing.
              // Omitted, never guessed, when Prebid reports none.
              prebidVersion: prebidVersionOf(this.pbjs),
            }),
          (e) => this.onError("sendBids failed", e),
        );
      };
      let grace;
      const deadline = this.setTimeoutFn(send, REPORT_DEADLINE_MS);
      c.onSettled = () => {
        if (sent || grace !== undefined) return;
        grace = this.setTimeoutFn(send, WIN_GRACE_MS);
      };
      c.onWon = () => send();
      if (c.rendered) c.onSettled();
      // A page that closes first still reports what it saw.
      c.flush = send;
    }
  }

  /** Wire Prebid and GPT events into THIS auction's collectors only.
   *
   *  Every event is matched by `auctionId` and `adUnitCode`. Prebid's handlers
   *  are page-global and outlive the auction, so without the match one slot's
   *  bid lands in every other slot's batch, and a later auction's bids land in
   *  this one's. */
  #subscribe(auctionId, collectors) {
    const target = (bid) =>
      bid && bid.auctionId === auctionId ? collectors.get(bid.adUnitCode) : undefined;
    const on = (evt, handler) =>
      swallow(
        () =>
          this.pbjs?.onEvent?.(evt, (arg) =>
            swallow(() => handler(arg), (e) => this.onError(`collector ${evt}`, e)),
          ),
        (e) => this.onError(`onEvent ${evt}`, e),
      );
    if (collectors.size === 0) return;
    on("bidResponse", (bid) => target(bid)?.onBidResponse(bid));
    on("noBid", (bid) => target(bid)?.onNoBid(bid));
    on("bidTimeout", (list) => {
      for (const b of list ?? []) target(b)?.onBidTimeout([b]);
    });
    on("bidWon", (bid) => {
      const c = target(bid);
      if (!c) return;
      c.onBidWon(bid);
      c.onWon?.();
    });
    swallow(
      () =>
        this.googletag?.pubads?.().addEventListener?.("slotRenderEnded", (e) => {
          const divId = swallow(() => e?.slot?.getSlotElementId?.());
          const c = collectors.get(divId);
          if (!c || c.rendered) return;
          swallow(
            () =>
              c.onSlotRenderEnded({
                isEmpty: e?.isEmpty === true,
                lineItemId: e?.lineItemId ?? null,
                adUnitPath: swallow(() => e?.slot?.getAdUnitPath?.()),
              }),
            (err) => this.onError("slotRenderEnded", err),
          );
          c.onSettled?.();
        }),
      (e) => this.onError("addEventListener", e),
    );
  }

  /** Write targeting within budget, then refresh every slot in one call. */
  #applyTargetingAndRefresh(units, withBidders, collectors, consent) {
    if (withBidders.length > 0 && typeof this.pbjs?.setTargetingForGPTAsync === "function") {
      // Scoped to this auction's slots. Unscoped, a later single-slot auction
      // would reset the targeting of slots that already rendered.
      swallow(
        () => this.pbjs.setTargetingForGPTAsync(withBidders.map((u) => u.divId)),
        (e) => this.onError("setTargeting", e),
      );
    }
    const gtSlots = [];
    for (const u of units) {
      const slot = this.#findSlot(u.divId);
      if (!slot) continue;
      gtSlots.push(slot);
      const c = collectors.get(u.divId);
      if (!c) continue;
      const current = swallow(() => slot.getTargetingKeys?.() ?? []) ?? [];
      const targeting = {};
      for (const k of current) {
        const v = swallow(() => slot.getTargeting?.(k));
        if (v != null) targeting[k] = Array.isArray(v) ? v[0] : v;
      }
      const bidders = (u.bidders ?? []).map((b) => b.bidder);
      const cpmByBidder = {};
      for (const b of c.toBatch().bids ?? []) {
        if (b.cpmMicros != null) cpmByBidder[b.bidder] = b.cpmMicros;
      }
      const fitted = fitToBudget(targeting, { bidders, cpmByBidder });
      if (fitted.shedBidders.length > 0) {
        c.onKeyValuesShed(fitted.shedBidders);
        for (const k of Object.keys(targeting)) {
          if (!(k in fitted.targeting)) swallow(() => slot.clearTargeting?.(k));
        }
      }
    }
    // npa=1 whenever consent is not affirmative. Google then serves
    // non-personalised demand rather than nothing — the revenue-preserving legal
    // path, and why no-consent traffic is not worth zero (D4).
    if (consent?.npa) {
      swallow(() => this.googletag?.pubads?.().setPrivacySettings?.({ nonPersonalizedAds: true }));
    }
    if (gtSlots.length > 0) {
      swallow(() => this.requestAds(gtSlots), (e) => this.onError("gpt refresh", e));
    }
  }

  #findSlot(divId) {
    const slots = swallow(() => this.googletag?.pubads?.().getSlots?.()) ?? [];
    return slots.find((s) => swallow(() => s.getSlotElementId?.()) === divId);
  }
}

/** Map one Playbid unit onto a Prebid ad unit.
 *
 *  Outstream video carries its own renderer: Prebid calls it when GAM serves
 *  the Prebid creative for a video win, and it plays the VAST in our player.
 *  A `url` is required for Prebid to prefer an ad-unit renderer, but it is
 *  never loaded — the render function is already on the page. */
export function toPrebidAdUnit(u) {
  const isVideo = u.mediaType === "video";
  const mediaTypes = isVideo
    ? {
        video: {
          mimes: ["video/mp4"],
          protocols: [2, 3, 5, 6],
          plcmt: 4,
          // Adapters silently drop a video slot missing these: Index Exchange
          // (min/maxduration), Magnite (linearity), Criteo (api, skip,
          // playbackmethod). The platform always sends them; this covers an
          // older config.
          minduration: 1,
          maxduration: 60,
          linearity: 1,
          skip: 0,
          api: [],
          playbackmethod: [2],
          ...(u.video ?? {}),
          context: "outstream",
          playerSize: u.sizes?.[0] ? [u.sizes[0]] : u.video?.playerSize ? [u.video.playerSize] : [[640, 480]],
        },
      }
    : { banner: { sizes: u.sizes ?? [] } };
  return {
    code: u.divId,
    mediaTypes,
    bids: (u.bidders ?? []).map((b) => ({ bidder: b.bidder, params: b.params ?? {} })),
    ...(isVideo && u.renderer ? { renderer: u.renderer } : {}),
    ...(u.floorCpm != null
      ? { floors: { currency: "USD", schema: { fields: ["mediaType"] }, values: { "*": u.floorCpm } } }
      : {}),
  };
}
